/**
 * StaffVMs — IT-SCE Intern: VMs aller Kunden + Assignment.
 *
 * TQ #9697 (Staff-Migration Teil D, Bundle #9523). Portiert aus
 * customer-portal/src/pages/staff/StaffVMs.tsx — gleiche API-Calls
 * (api.staffListVMs/staffAssignVM/staffUnassignVM/…), Styling an
 * StaffKnowledge.tsx (org-portal, TQ #7128) angeglichen.
 *
 * Routing: outer-router-level (App.tsx) unter /staff/vms, hinter
 * StaffOnly-Guard, gerendert über StaffHealthHub (Tab "VMs").
 */

import { useEffect, useState, useCallback } from 'react'
import { api, StaffVM, StaffVMStats } from '../../services/api'
import { useToast } from '../../components/ui'

const STATUS_DOT: Record<string, string> = {
  running: '#10b981',
  stopped: '#ef4444',
  paused: '#f59e0b',
  unknown: '#9ca3af',
}

function fmtMem(mb: number | undefined) {
  if (!mb) return '—'
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`
  return `${mb} MB`
}

interface AssignForm {
  vmId: number
  vmName: string
  type: 'customer' | 'organization'
  targetId: string
  monthlyPrice: string
  notes: string
}

export default function StaffVMs() {
  const [vms, setVMs] = useState<StaffVM[]>([])
  const [stats, setStats] = useState<StaffVMStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showOnlyUnassigned, setShowOnlyUnassigned] = useState(false)
  const [assignForm, setAssignForm] = useState<AssignForm | null>(null)
  const [assigning, setAssigning] = useState(false)
  const { showToast } = useToast()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [listResp, statsResp] = await Promise.all([
        showOnlyUnassigned ? api.staffListUnassignedVMs() : api.staffListVMs(),
        api.staffGetVMStats().catch(() => null),
      ])
      setVMs(listResp.vms ?? [])
      const inlineStats = (listResp as { stats?: typeof stats }).stats
      setStats(statsResp?.stats ?? inlineStats ?? null)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string })?.error ?? err)
      showToast('error', `Laden fehlgeschlagen: ${msg}`)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnlyUnassigned])
  useEffect(() => { load() }, [load])

  async function handleAssign() {
    if (!assignForm || assigning) return
    const targetId = parseInt(assignForm.targetId)
    if (!targetId) { showToast('error', 'Bitte eine gültige Ziel-ID eingeben'); return }
    setAssigning(true)
    try {
      await api.staffAssignVM(assignForm.vmId, {
        type: assignForm.type,
        target_id: targetId,
        monthly_price: assignForm.monthlyPrice ? parseFloat(assignForm.monthlyPrice) : undefined,
        notes: assignForm.notes || undefined,
      })
      showToast('success', `VM "${assignForm.vmName}" erfolgreich zugewiesen`)
      setAssignForm(null)
      load()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string })?.error ?? err)
      showToast('error', `Zuweisung fehlgeschlagen: ${msg}`)
    } finally {
      setAssigning(false)
    }
  }

  async function handleUnassign(vm: StaffVM) {
    if (!confirm(`VM "${vm.vm_name}" Zuweisung aufheben?`)) return
    try {
      await api.staffUnassignVM(vm.id)
      showToast('success', `VM "${vm.vm_name}" Zuweisung aufgehoben`)
      load()
    } catch (err) {
      const msg = err instanceof Error ? err.message : String((err as { error?: string })?.error ?? err)
      showToast('error', `Aufheben fehlgeschlagen: ${msg}`)
    }
  }

  const filtered = vms.filter(v => {
    if (!search) return true
    const q = search.toLowerCase()
    return (v.vm_name + ' ' + (v.hostname ?? '') + ' ' + (v.ip_address ?? '') + ' ' + (v.customer_email ?? '') + ' ' + (v.organization_name ?? '')).toLowerCase().includes(q)
  })

  return (
    <div>
      <p style={{ color: '#666', marginTop: 0, marginBottom: '1rem', fontSize: '0.9rem' }}>
        VMs aller Kunden + Assignment ({vms.length})
      </p>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
          <Stat label="Gesamt" value={String(stats.total ?? 0)} />
          <Stat label="Running" value={String(stats.running ?? 0)} color={STATUS_DOT.running} />
          <Stat label="Stopped" value={String(stats.stopped ?? 0)} color={STATUS_DOT.stopped} />
          {stats.suspended != null && stats.suspended > 0 && (
            <Stat label="Suspended" value={String(stats.suspended)} color={STATUS_DOT.paused} />
          )}
          {stats.assigned != null && (
            <Stat label="Zugewiesen" value={String(stats.assigned)} />
          )}
          <Stat label="Unzugewiesen" value={String(stats.unassigned ?? 0)} color={(stats.unassigned ?? 0) > 0 ? '#f59e0b' : '#9ca3af'} />
          {stats.total_cpu_cores != null && (
            <Stat label="vCPU gesamt" value={String(stats.total_cpu_cores)} />
          )}
          {stats.total_memory_gb != null && (
            <Stat label="RAM gesamt" value={`${stats.total_memory_gb} GB`} />
          )}
        </div>
      )}

      {!loading && stats?.total === 0 && (
        <div style={{ padding: 12, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, marginBottom: 16, fontSize: 13 }}>
          ⚠ Noch keine VMs in der DB. <code>user_vms</code>-Tabelle ist leer — Proxmox-Sync-Job
          (admin-api → Proxmox) muss laufen damit z.B. der BuM-Server (192.168.25.207) erscheint.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Suche VM, Hostname, IP, Kunde…"
          style={{ flex: 1, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={showOnlyUnassigned} onChange={e => setShowOnlyUnassigned(e.target.checked)} />
          Nur unzugewiesene
        </label>
      </div>

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Lade VMs…</div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>VM</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>OS / Hostname</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>IP</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>vCPU</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>RAM</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Disk</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Zugeordnet</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Preis/Mo</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Abrechnung</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ fontWeight: 500 }}>{v.vm_name}</div>
                    <div style={{ color: '#9ca3af', fontSize: 11 }}>VMID {v.vm_id}</div>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#6b7280' }}>
                    {v.operating_system ?? '—'}
                    {v.hostname && <div style={{ fontSize: 11, color: '#9ca3af' }}>{v.hostname}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace' }}>{v.ip_address ?? '—'}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{v.cpu_cores ?? '—'}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{fmtMem(v.memory_mb)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>{v.disk_gb ? `${v.disk_gb} GB` : '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {v.organization_name ? (
                      <div>
                        <div>{v.organization_name}</div>
                        {v.customer_email && <div style={{ color: '#9ca3af', fontSize: 11 }}>{v.customer_email}</div>}
                      </div>
                    ) : v.customer_email ? (
                      <div style={{ color: '#6b7280' }}>{v.customer_email}</div>
                    ) : (
                      <span style={{ color: '#f59e0b', fontSize: 12 }}>(unzugewiesen)</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {v.monthly_price != null && v.monthly_price > 0
                      ? v.monthly_price.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
                      : <span style={{ color: '#9ca3af' }}>—</span>}
                    {v.billing_mode === 'internal' && v.cost_rate != null && (
                      <div style={{ color: '#9ca3af', fontSize: 11 }}>
                        SK {v.cost_rate.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {v.billing_mode === 'internal' ? (
                      <span style={{ padding: '2px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: 12, fontSize: 11 }}>
                        Verrechnung
                      </span>
                    ) : v.billing_mode === 'reseller' ? (
                      <span style={{ padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 12, fontSize: 11 }}>
                        Reseller
                      </span>
                    ) : (
                      <span style={{ padding: '2px 8px', background: '#f3f4f6', color: '#374151', borderRadius: 12, fontSize: 11 }}>
                        Stripe
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_DOT[v.status] ?? STATUS_DOT.unknown }} />
                      {v.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                    {v.customer_id || v.organization_id ? (
                      <button
                        onClick={() => handleUnassign(v)}
                        style={{ padding: '4px 10px', fontSize: 12, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer' }}
                      >
                        Aufheben
                      </button>
                    ) : (
                      <button
                        onClick={() => setAssignForm({ vmId: v.id, vmName: v.vm_name, type: 'customer', targetId: '', monthlyPrice: '', notes: '' })}
                        style={{ padding: '4px 10px', fontSize: 12, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer' }}
                      >
                        Zuweisen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={11} style={{ padding: 32, textAlign: 'center', color: '#9ca3af' }}>Keine VMs gefunden.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* VM Assign Modal */}
      {assignForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 400, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 700 }}>VM zuweisen: {assignForm.vmName}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ fontSize: 13 }}>
                Ziel-Typ
                <select
                  value={assignForm.type}
                  onChange={e => setAssignForm(f => f ? { ...f, type: e.target.value as 'customer' | 'organization' } : f)}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
                >
                  <option value="customer">Kunde</option>
                  <option value="organization">Organisation</option>
                </select>
              </label>
              <label style={{ fontSize: 13 }}>
                {assignForm.type === 'customer' ? 'Kunden-ID' : 'Organisations-ID'}
                <input
                  type="number"
                  value={assignForm.targetId}
                  onChange={e => setAssignForm(f => f ? { ...f, targetId: e.target.value } : f)}
                  placeholder="Numerische ID"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ fontSize: 13 }}>
                Monatspreis (€, optional)
                <input
                  type="number"
                  step="0.01"
                  value={assignForm.monthlyPrice}
                  onChange={e => setAssignForm(f => f ? { ...f, monthlyPrice: e.target.value } : f)}
                  placeholder="z.B. 29.90"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              </label>
              <label style={{ fontSize: 13 }}>
                Notiz (optional)
                <input
                  type="text"
                  value={assignForm.notes}
                  onChange={e => setAssignForm(f => f ? { ...f, notes: e.target.value } : f)}
                  placeholder="z.B. BuM-Server"
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAssignForm(null)}
                style={{ padding: '8px 16px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleAssign}
                disabled={assigning}
                style={{ padding: '8px 16px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, cursor: assigning ? 'default' : 'pointer', opacity: assigning ? 0.7 : 1, fontSize: 13 }}
              >
                {assigning ? 'Weise zu…' : 'Zuweisen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ padding: 14, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <div style={{ color: '#6b7280', fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: color ?? '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
        {color && <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />}
        {value}
      </div>
    </div>
  )
}
